/** @jsxImportSource @emotion/react */
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { selectTeams } from 'modules/main/store/main-page';
import RankedBarList from 'modules/main/ui/components/section/components/ranked-bar-list/ranked-bar-list';
import ValueGroup from 'modules/main/ui/components/section/components/value-group/value-group';
import Section from 'modules/main/ui/components/section/section';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { theme } from 'styles/theme/theme';
import type { DivProps } from 'typings';
import TeamsAddIcon from 'ud-ui/components/icon/teams-add-icon';
import Text from 'ud-ui/components/text';

interface IProps extends DivProps {
  loading?: boolean;
}

function SectionTeams(props: IProps) {
  const { loading, ...rest } = props;
  const { t } = useLingui();
  const navigate = useNavigate();
  const { total, declared, popular } = useSelector(selectTeams);

  const values = [
    { label: t`всего`, value: total.toString() },
    { label: t`заявлены`, value: declared.toString() },
  ];

  const items = popular.map((team) => ({
    id: team.id,
    image: team.image,
    label: team.name,
    value: team.matchCount,
    valueText: <Plural value={team.matchCount} one="# матч" few="# матча" many="# матчей" other="# матча" />,
  }));

  // Команда без id (бэкенд не прислал `team`) — открывать нечего.
  const handleTeamClick = (item: { id: number | string }) => {
    const teamId = Number(item.id);
    if (teamId) navigate(Pages.TEAMS.builders.edit(teamId));
  };

  return (
    <Section
      icon={<TeamsAddIcon />}
      header={t`Команды`}
      buttonText={t`Добавить команду`}
      isShowButton={total === 0}
      onClickButton={() => navigate(Pages.TEAMS.CREATE)}
      onClickExtend={() => navigate(Pages.TEAMS.INDEX)}
      loading={loading}
      {...rest}
    >
      <ValueGroup values={values} className="mb-5" />
      {items.length ? (
        <div className="mt-1 flex flex-1 flex-col gap-3">
          <div className="flex flex-col">
            <Text variant="body-1" color={theme.colors.InkPrimary} className="mt-2.5">
              <Trans>Популярные</Trans>
            </Text>
            <Text variant="subtitle-2" color={theme.colors.WhiteGray} className="mt-1">
              <Trans>Часто участвуют в турнирах</Trans>
            </Text>
          </div>
          <RankedBarList items={items} onItemClick={handleTeamClick} />
        </div>
      ) : (
        <div className="flex flex-col">
          <Text variant="body-1" color={theme.colors.InkPrimary} className="mt-2.5">
            <Trans>Популярные</Trans>
          </Text>
          <Text variant="subtitle-2" color={theme.colors.WhiteGray} className="mt-1">
            <Trans>В списке нет команд</Trans>
          </Text>
        </div>
      )}
    </Section>
  );
}

export default SectionTeams;
